/**
 * The KnowIt adapter for document merge, revision parts and restore (W9 §A.2, §C.3).
 *
 * `MergeController` and the RPC half of `RevisionsController` answer the ordinary `{data}`
 * envelope, except `documents.revision.restore` — three top-level keys (`data`,
 * `restoredRevisionId`, `policies`), never a pure envelope — so this module reads the raw
 * body rather than the shared `Envelope<T>` type everywhere else in the estate assumes.
 *
 * `documents.merge.commit`'s refusal is data, not just a status: a 409 body carries
 * `{error, unresolved}`, and `unresolved` is delivered NOWHERE else (§E-7) — kms read it off a
 * 200 the controller never sends, so its "N parts still need a decision" toast could never
 * fire. `commitStatus` on the transport is what lets this adapter see that body even though
 * the response is not `ok`.
 */
import { ApiError, ForbiddenError, MergeUnresolvedError, NotFoundError } from "~/api/errors";
import type {
  Divergence,
  MergeAudit,
  MergeCommitResult,
  MergeDecision,
  MergePartDiff,
  MergeSession,
  Revision,
  RevisionPart,
} from "~/api/types";
import {
  DivergenceSchema,
  MergeAuditSchema,
  MergePartDiffSchema,
  MergeSessionSchema,
  RevisionPartSchema,
  RevisionSchema,
} from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export interface MergeTransport {
  /** `{data}` (occasionally `{data, restoredRevisionId, policies}`) — the raw, un-typed body. */
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** Status-preserving: `merge.commit`'s 409 body is data (§E-7), so it must not be thrown away
   * by a generic error mapper before this module can read `unresolved` off it. */
  commitStatus(sessionId: string): Promise<{ status: number; body: unknown }>;
}

export function createKnowItMerge(transport: MergeTransport) {
  return {
    divergence: async (documentId: string): Promise<Divergence> => {
      const raw = obj(await transport.post("documents.divergence", { id: documentId }));
      return DivergenceSchema.parse(obj(raw.data));
    },

    open: async (input: {
      documentId: string;
      contributingRevisionIds: string[];
    }): Promise<MergeSession> => {
      const raw = obj(await transport.post("documents.merge.open", input));
      return MergeSessionSchema.parse(obj(raw.data));
    },

    preview: async (sessionId: string): Promise<MergePartDiff[]> => {
      const raw = obj(await transport.post("documents.merge.preview", { sessionId }));
      return arr(raw.data).map((entry) => MergePartDiffSchema.parse(entry));
    },

    resolvePart: async (input: {
      sessionId: string;
      partId: string;
      decision: MergeDecision;
      chosenFrom?: string;
      combinedContent?: string;
    }): Promise<void> => {
      await transport.post("documents.merge.resolvePart", {
        sessionId: input.sessionId,
        partId: input.partId,
        decision: input.decision,
        ...(input.chosenFrom ? { chosenFrom: input.chosenFrom } : {}),
        ...(input.combinedContent ? { combinedContent: input.combinedContent } : {}),
      });
    },

    /**
     * §E-7: the ONLY place `unresolved` is delivered is the 409 body — never the 200 kms's own
     * store waited for. `commitStatus` hands this module the body regardless of status, so
     * that count reaches the panel instead of a toast that can never fire.
     */
    commit: async (sessionId: string): Promise<MergeCommitResult> => {
      const { status, body } = await transport.commitStatus(sessionId);
      const payload = obj(body);
      if (status === 409) {
        const unresolved = typeof payload.unresolved === "number" ? payload.unresolved : 0;
        throw new MergeUnresolvedError(
          unresolved,
          typeof payload.error === "string" ? payload.error : "Unresolved parts remain.",
        );
      }
      if (status === 403) {
        throw new ForbiddenError(typeof payload.error === "string" ? payload.error : undefined);
      }
      if (status === 404) {
        throw new NotFoundError(typeof payload.error === "string" ? payload.error : undefined);
      }
      if (status < 200 || status >= 300) {
        throw new ApiError(
          typeof payload.error === "string" ? payload.error : "Merge commit failed",
          status,
        );
      }
      return parseCommitResult(obj(payload.data));
    },

    audit: async (sessionId: string): Promise<MergeAudit> => {
      const raw = obj(await transport.post("documents.merge.audit", { sessionId }));
      return MergeAuditSchema.parse(obj(raw.data));
    },

    revisionParts: async (revisionId: string): Promise<RevisionPart[]> => {
      const raw = obj(await transport.post("revisions.parts", { revisionId }));
      return arr(raw.data).map((entry) => RevisionPartSchema.parse(entry));
    },

    /**
     * `documents.revision.restore` — three top-level keys, never a pure envelope: `data` (the
     * updated document, read by `useDocument`'s own invalidation, not parsed here), the newly
     * written `restoredRevisionId`, and `policies` (unused here — the document query re-reads
     * abilities on its own refetch).
     */
    restoreRevision: async (input: {
      documentId: string;
      revisionId: string;
      partId?: string;
    }): Promise<{ restoredRevisionId: string }> => {
      const raw = obj(
        await transport.post("documents.revision.restore", {
          documentId: input.documentId,
          revisionId: input.revisionId,
          ...(input.partId ? { partId: input.partId } : {}),
        }),
      );
      return { restoredRevisionId: String(raw.restoredRevisionId ?? "") };
    },

    revisionsDiff: async (input: {
      id: string;
      compareToId?: string;
      partId?: string;
    }): Promise<unknown> => {
      const raw = obj(
        await transport.post("revisions.diff", {
          id: input.id,
          ...(input.compareToId ? { compareToId: input.compareToId } : {}),
          ...(input.partId ? { partId: input.partId } : {}),
        }),
      );
      return raw.data;
    },

    revisionInfo: async (revisionId: string): Promise<Revision> => {
      const raw = obj(await transport.post("revisions.info", { id: revisionId }));
      return RevisionSchema.parse(obj(raw.data));
    },
  };
}

function parseCommitResult(raw: Json): MergeCommitResult {
  return {
    resultRevisionId: String(raw.resultRevisionId ?? ""),
    accepted: typeof raw.accepted === "number" ? raw.accepted : 0,
    rejected: typeof raw.rejected === "number" ? raw.rejected : 0,
    combined: typeof raw.combined === "number" ? raw.combined : 0,
    unresolved: typeof raw.unresolved === "number" ? raw.unresolved : 0,
  };
}
